import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from '../context/TranslationContext';
import { Navbar } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Label } from '../components/ui/label';
import { Progress } from '../components/ui/progress';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
  ChevronLeft, ChevronRight, Check, X, Award,
  Clock, AlertCircle, Trophy, RotateCcw, ShieldAlert
} from 'lucide-react';

import { safeArray } from '../lib/utils';

import { getApiBaseUrl } from '@/lib/apiBase';
const API_URL = getApiBaseUrl();

const MAX_TAB_VIOLATIONS = 3;
const VIOLATION_DEBOUNCE_MS = 800;

const QuizPage = () => {
  const { lessonId } = useParams();
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const { t, tFormat } = useTranslation();
  const [quiz, setQuiz] = useState(null);
  const [fullQuiz, setFullQuiz] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [submitted, setSubmitted] = useState(false);
  const [result, setResult] = useState(null);

  // Timer state
  const [timeRemaining, setTimeRemaining] = useState(null);
  const [tabVisible, setTabVisible] = useState(true);

  // Anti-cheat state
  const [violationCount, setViolationCount] = useState(0);
  const [showWarning, setShowWarning] = useState(false);
  const [autoSubmittedReason, setAutoSubmittedReason] = useState(null);

  const answersRef = useRef(answers);
  const lastViolationTimeRef = useRef(0);
  const autoSubmittedRef = useRef(false);

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    if (!token) {
      navigate('/login');
      return;
    }
    fetchQuiz();
  }, [lessonId, token]);

  useEffect(() => {
    answersRef.current = answers;
  }, [answers]);

  const fetchQuiz = async () => {
    try {
      const res = await axios.get(`${API_URL}/quiz/${lessonId}`, { headers });
      setQuiz(res.data);
      const qs = safeArray(res.data?.questions);
      setAnswers(new Array(qs.length).fill(-1));
    } catch (err) {
      if (err.response?.status === 404) {
        toast.error(t('toast_quiz_no', 'No quiz available for this lesson'));
        navigate(-1);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAnswer = (index) => {
    const newAnswers = [...answers];
    newAnswers[currentQuestion] = index;
    setAnswers(newAnswers);
  };

  const submitQuizWithAnswers = async (answersToSubmit, violations) => {
    if (submitted || autoSubmittedRef.current) return;
    autoSubmittedRef.current = true;
    try {
      const res = await axios.post(`${API_URL}/quiz/submit`, {
        quiz_id: quiz.id,
        answers: answersToSubmit,
        violations_count: violations
      }, { headers });

      setResult(res.data);

      // Fetch full quiz with answers
      const fullRes = await axios.get(`${API_URL}/quiz/${lessonId}/results`, { headers });
      setFullQuiz(fullRes.data.quiz);
      setSubmitted(true);

      if (res.data.passed) {
        toast.success(
          tFormat(
            'quiz_toast_passed',
            { points: res.data.points_earned },
            `Congratulations! You passed! +${res.data.points_earned} points`
          )
        );
      } else {
        toast.info(
          tFormat(
            'quiz_toast_done',
            { points: res.data.points_earned },
            `Quiz completed. +${res.data.points_earned} points`
          )
        );
      }
    } catch (err) {
      autoSubmittedRef.current = false;
      toast.error(t('toast_quiz_submit_fail', 'Failed to submit quiz'));
    }
  };

  const handleSubmit = async () => {
    if (answers.some(a => a === -1)) {
      toast.error(t('toast_quiz_answer_all', 'Please answer all questions'));
      return;
    }
    await submitQuizWithAnswers(answers, violationCount);
  };

  const handleTimeUp = () => {
    const questionCount = safeArray(quiz?.questions).length;
    const newAnswers = [...answers];
    newAnswers[currentQuestion] = -1; // unanswered -> counts as wrong
    setAnswers(newAnswers);

    if (currentQuestion < questionCount - 1) {
      setCurrentQuestion(q => q + 1);
    } else {
      submitQuizWithAnswers(newAnswers, violationCount);
    }
  };

  const registerViolation = () => {
    if (!quiz?.anti_cheat_enabled || submitted || autoSubmittedRef.current) return;
    const now = Date.now();
    // visibilitychange + window blur commonly fire together for the same
    // tab-switch event — debounce so one switch doesn't count twice.
    if (now - lastViolationTimeRef.current < VIOLATION_DEBOUNCE_MS) return;
    lastViolationTimeRef.current = now;

    setViolationCount(prev => {
      const next = prev + 1;
      if (next >= MAX_TAB_VIOLATIONS) {
        setShowWarning(false);
        setAutoSubmittedReason('violations');
        toast.error(t('quiz_auto_submitted_violations', 'Quiz auto-submitted due to multiple tab switches'));
        submitQuizWithAnswers(answersRef.current, next);
      } else {
        setShowWarning(true);
      }
      return next;
    });
  };

  const handleRetry = () => {
    setAnswers(new Array(safeArray(quiz?.questions).length).fill(-1));
    setCurrentQuestion(0);
    setSubmitted(false);
    setResult(null);
    setViolationCount(0);
    setShowWarning(false);
    setAutoSubmittedReason(null);
    autoSubmittedRef.current = false;
    setFullQuiz(null);
  };

  // Timer: reset countdown on every new question while the quiz is active
  useEffect(() => {
    if (!quiz?.timer_enabled || submitted) {
      setTimeRemaining(null);
      return;
    }
    setTimeRemaining(quiz.time_per_question || 60);
  }, [currentQuestion, quiz?.timer_enabled, quiz?.time_per_question, submitted]);

  // Timer: tick every second, paused while the tab is hidden
  useEffect(() => {
    if (!quiz?.timer_enabled || submitted || timeRemaining === null) return;
    if (!tabVisible) return; // paused while tab is hidden
    if (timeRemaining <= 0) {
      handleTimeUp();
      return;
    }
    const id = setTimeout(() => setTimeRemaining(t => (t !== null ? t - 1 : t)), 1000);
    return () => clearTimeout(id);
  }, [timeRemaining, tabVisible, quiz?.timer_enabled, submitted]);

  // Anti-cheat: tab-switch / new-window detection (only during an active attempt)
  useEffect(() => {
    const handleVisibility = () => {
      const hidden = document.hidden;
      setTabVisible(!hidden);
      if (hidden) registerViolation();
    };
    const handleBlur = () => registerViolation();

    document.addEventListener('visibilitychange', handleVisibility);
    window.addEventListener('blur', handleBlur);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('blur', handleBlur);
    };
  }, [quiz?.anti_cheat_enabled, submitted, violationCount]);

  // Anti-cheat: block copy/cut/context-menu and devtools/copy keyboard shortcuts
  useEffect(() => {
    if (!quiz?.anti_cheat_enabled || submitted) return;

    const blockEvent = (e) => e.preventDefault();
    const handleKeyDown = (e) => {
      const key = e.key?.toLowerCase();
      const mod = e.ctrlKey || e.metaKey;
      if (mod && ['c', 'v', 'a', 'p'].includes(key)) {
        e.preventDefault();
      } else if (key === 'f12') {
        e.preventDefault();
      } else if (mod && e.shiftKey && key === 'i') {
        e.preventDefault();
      }
    };

    document.addEventListener('copy', blockEvent);
    document.addEventListener('cut', blockEvent);
    document.addEventListener('contextmenu', blockEvent);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('copy', blockEvent);
      document.removeEventListener('cut', blockEvent);
      document.removeEventListener('contextmenu', blockEvent);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [quiz?.anti_cheat_enabled, submitted]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-pulse text-muted-foreground">{t('quiz_loading', 'Loading quiz...')}</div>
      </div>
    );
  }

  if (!quiz) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
          <h2 className="text-xl font-semibold mb-4">{t('quiz_not_available', 'Quiz not available')}</h2>
          <Button onClick={() => navigate(-1)}>{t('quiz_go_back', 'Go Back')}</Button>
        </div>
      </div>
    );
  }

  const questions = safeArray(submitted && fullQuiz ? fullQuiz?.questions : quiz?.questions);
  const question = questions[currentQuestion];
  const progressPercent = questions.length
    ? ((currentQuestion + 1) / questions.length) * 100
    : 0;

  if (!questions.length) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
          <h2 className="text-xl font-semibold mb-4">
            {t('quiz_no_questions', 'This quiz has no questions')}
          </h2>
          <Button onClick={() => navigate(-1)}>{t('quiz_go_back', 'Go Back')}</Button>
        </div>
      </div>
    );
  }

  if (!question) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
          <h2 className="text-xl font-semibold mb-4">{t('quiz_invalid_question', 'Invalid question')}</h2>
          <Button onClick={() => navigate(-1)}>{t('quiz_go_back', 'Go Back')}</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="quiz-page">
      <Navbar />
      
      <main className="pt-20 pb-12">
        <div className="max-w-3xl mx-auto px-6">
          {/* Header */}
          <div className="mb-8">
            <Button 
              variant="ghost" 
              className="mb-4"
              onClick={() => navigate(-1)}
            >
              <ChevronLeft className="w-4 h-4 mr-2" />
              {t('quiz_back_lesson', 'Back to Lesson')}
            </Button>
            <h1 className="text-3xl font-serif font-medium mb-2">
              {quiz?.title ?? t('quiz_default_title', 'Quiz')}
            </h1>
            <div className="flex items-center gap-4 text-sm text-muted-foreground">
              <span className="flex items-center gap-1">
                <Clock className="w-4 h-4" />
                {questions.length} {t('quiz_questions_count', 'questions')}
              </span>
              <span className="flex items-center gap-1">
                <Award className="w-4 h-4" />
                {t('quiz_passing_score', 'Passing score:')} {quiz?.passing_score ?? 70}%
              </span>
            </div>
          </div>

          {/* Result Card */}
          {submitted && autoSubmittedReason === 'violations' && (
            <div className="mb-6 p-4 rounded-lg border border-red-500/50 bg-red-500/5 flex items-center gap-3 text-sm">
              <ShieldAlert className="w-5 h-5 text-red-500 shrink-0" />
              <span>{t('quiz_auto_submitted_violations', 'Quiz auto-submitted due to multiple tab switches')}</span>
            </div>
          )}

          {submitted && result && (
            <Card className={`mb-8 ${result.passed ? 'border-green-500/50 bg-green-500/5' : 'border-amber-500/50 bg-amber-500/5'}`}>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className={`w-16 h-16 rounded-full flex items-center justify-center ${
                      result.passed ? 'bg-green-500/20 text-green-500' : 'bg-amber-500/20 text-amber-500'
                    }`}>
                      {result.passed ? <Trophy className="w-8 h-8" /> : <AlertCircle className="w-8 h-8" />}
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold">
                        {result.passed
                          ? t('quiz_congrats_title', 'Congratulations!')
                          : t('quiz_completed_title', 'Quiz Completed')}
                      </h3>
                      <p className="text-muted-foreground">
                        {t('quiz_score_label', 'Score:')}{' '}
                        {tFormat(
                          'quiz_score_detail',
                          {
                            score: result.score,
                            correct: result.correct_answers,
                            total: result.total_questions,
                          },
                          `${result.score}% (${result.correct_answers}/${result.total_questions} correct)`
                        )}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <Badge className={result.passed ? 'bg-green-500' : 'bg-amber-500'}>
                      {tFormat(
                        'quiz_points_earned',
                        { n: result.points_earned },
                        `+${result.points_earned} points`
                      )}
                    </Badge>
                    <Button variant="outline" size="sm" className="mt-2" onClick={handleRetry}>
                      <RotateCcw className="w-4 h-4 mr-2" />
                      {t('quiz_retry', 'Retry')}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Progress */}
          <div className="mb-6">
            <div className="flex items-center justify-between text-sm mb-2">
              <span>
                {t('quiz_question_label', 'Question')} {currentQuestion + 1} {t('quiz_of', 'of')}{' '}
                {questions.length}
              </span>
              <div className="flex items-center gap-3">
                {quiz?.timer_enabled && !submitted && timeRemaining !== null && (
                  <span
                    className={`flex items-center gap-1 font-medium ${timeRemaining < 10 ? 'text-red-500' : ''}`}
                    data-testid="quiz-timer"
                  >
                    <Clock className="w-4 h-4" />
                    {Math.floor(timeRemaining / 60)}:{String(timeRemaining % 60).padStart(2, '0')}{' '}
                    {t('quiz_timer_remaining', 'remaining')}
                  </span>
                )}
                <span>
                  {Math.round(progressPercent)}
                  {t('quiz_percent_complete', '% complete')}
                </span>
              </div>
            </div>
            <Progress
              value={quiz?.timer_enabled && !submitted && timeRemaining !== null
                ? (timeRemaining / (quiz.time_per_question || 60)) * 100
                : progressPercent}
              className={`h-2 ${quiz?.timer_enabled && !submitted && timeRemaining !== null && timeRemaining < 10 ? '[&>div]:bg-red-500' : ''}`}
            />
          </div>

          {/* Question Card */}
          <div
            className="relative"
            onCopy={(e) => quiz?.anti_cheat_enabled && !submitted && e.preventDefault()}
            onCut={(e) => quiz?.anti_cheat_enabled && !submitted && e.preventDefault()}
            onContextMenu={(e) => quiz?.anti_cheat_enabled && !submitted && e.preventDefault()}
          >
            <Card
              data-testid="question-card"
              className={quiz?.anti_cheat_enabled && !submitted ? 'select-none' : ''}
            >
              <CardHeader>
                <CardTitle className="text-xl font-medium">
                  {currentQuestion + 1}. {question.question}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <RadioGroup
                  value={answers[currentQuestion]?.toString()}
                  onValueChange={(v) => !submitted && handleAnswer(parseInt(v))}
                  className="space-y-3"
                >
                  {safeArray(question.options).map((option, index) => {
                    const isSelected = answers[currentQuestion] === index;
                    const correctKey = fullQuiz?.questions?.[currentQuestion]?.correct_answer;
                    const isCorrect =
                      submitted &&
                      fullQuiz &&
                      (index === correctKey || option === correctKey);
                    const isWrong = submitted && isSelected && !isCorrect;

                    return (
                      <div
                        key={index}
                        className={`flex items-center space-x-3 p-4 rounded-lg border transition-colors ${
                          isCorrect ? 'border-green-500 bg-green-500/10' :
                          isWrong ? 'border-red-500 bg-red-500/10' :
                          isSelected ? 'border-accent bg-accent/5' : 'hover:bg-muted'
                        }`}
                      >
                        <RadioGroupItem
                          value={index.toString()}
                          id={`option-${index}`}
                          disabled={submitted}
                          data-testid={`option-${index}`}
                        />
                        <Label
                          htmlFor={`option-${index}`}
                          className="flex-1 cursor-pointer flex items-center justify-between"
                        >
                          {option}
                          {submitted && (
                            isCorrect ? <Check className="w-5 h-5 text-green-500" /> :
                            isWrong ? <X className="w-5 h-5 text-red-500" /> : null
                          )}
                        </Label>
                      </div>
                    );
                  })}
                </RadioGroup>

                {/* Explanation */}
                {submitted && fullQuiz && fullQuiz.questions[currentQuestion].explanation && (
                  <div className="mt-6 p-4 bg-muted rounded-lg">
                    <h4 className="font-semibold mb-2">{t('quiz_explanation', 'Explanation')}</h4>
                    <p className="text-muted-foreground">{fullQuiz.questions[currentQuestion].explanation}</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Screenshot deterrent watermark — not a hard block, see PROGRESS.md */}
            {quiz?.anti_cheat_enabled && !submitted && (
              <div
                className="pointer-events-none select-none absolute inset-0 overflow-hidden flex flex-wrap content-around justify-around opacity-[0.06] z-10"
                aria-hidden="true"
              >
                {Array.from({ length: 12 }).map((_, i) => (
                  <span key={i} className="text-sm font-medium whitespace-nowrap -rotate-[30deg] px-6">
                    {user?.email || user?.name || 'THACHARI'}
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Navigation */}
          <div className="flex items-center justify-between mt-6">
            <Button
              variant="outline"
              onClick={() => setCurrentQuestion(q => q - 1)}
              disabled={currentQuestion === 0}
              data-testid="prev-question-btn"
            >
              <ChevronLeft className="w-4 h-4 mr-2" />
              {t('quiz_previous', 'Previous')}
            </Button>

            {currentQuestion === questions.length - 1 ? (
              submitted ? (
                <Button onClick={() => navigate(-1)} data-testid="finish-btn">
                  {t('quiz_back_lesson', 'Back to Lesson')}
                </Button>
              ) : (
                <Button 
                  onClick={handleSubmit}
                  className="bg-accent text-accent-foreground hover:bg-accent/90"
                  data-testid="submit-quiz-btn"
                >
                  {t('quiz_submit', 'Submit Quiz')}
                </Button>
              )
            ) : (
              <Button
                onClick={() => setCurrentQuestion(q => q + 1)}
                data-testid="next-question-btn"
              >
                {t('quiz_next', 'Next')}
                <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
            )}
          </div>
        </div>
      </main>

      {/* Anti-cheat warning overlay — informational only, does not block completion */}
      {showWarning && quiz?.anti_cheat_enabled && !submitted && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
          <Card className="max-w-sm border-red-500/50">
            <CardContent className="p-6 text-center space-y-4">
              <ShieldAlert className="w-10 h-10 mx-auto text-red-500" />
              <div>
                <h3 className="font-semibold text-lg">
                  {t('quiz_tab_switch_warning_title', 'Tab switching detected')}
                </h3>
                <p className="text-sm text-muted-foreground mt-1">
                  {t('quiz_tab_switch_warning_body', 'Return to the quiz to continue.')}
                </p>
              </div>
              <Badge variant="destructive">
                {tFormat(
                  'quiz_violation_count',
                  { count: violationCount, max: MAX_TAB_VIOLATIONS },
                  `${violationCount} of ${MAX_TAB_VIOLATIONS} warnings`
                )}
              </Badge>
              <Button onClick={() => setShowWarning(false)} className="w-full" data-testid="return-to-quiz-btn">
                {t('quiz_return_to_quiz', 'Return to Quiz')}
              </Button>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default QuizPage;
